import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from '../../../constants/apiConfig';

export type IncidentArea = {
  areaId?: UUID;
  incidentId: UUID;
  areaName: string;
  hazard: string;
  mitigations: string;
  riskGain: string | null;
};

export const keys = {
  base: 'areas',
  get all() {
    return [keys.base];
  },
  get: function (groupGuid: UUID, userGuid: UUID) {
    return [keys.base, groupGuid, userGuid];
  },
} as const;

export const useIncidentAreas = (incidentId: string) => {
  return useQuery({
    queryKey: ['areas', incidentId],
    queryFn: async () => {
      const resp = await axios.get<IncidentArea[]>(
        `${config.apiGateway.ics_incident_areas}/${incidentId}`
      );
      return resp.data;
    },
    enabled: !!incidentId,
    throwOnError: () => {
      return false;
    },
  });
};

export const useCreateIncidentArea = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (newIncidentArea: IncidentArea) => {
      const resp = await axios.post<IncidentArea>(
        `${config.apiGateway.ics_incident_areas}`,
        newIncidentArea
      );
      return resp.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.all });
    },
  });
};

export const useUpdateIncidentArea = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (updatedIncidentArea: IncidentArea) => {
      const resp = await axios.put<IncidentArea>(
        `${config.apiGateway.ics_incident_areas}/${updatedIncidentArea.areaId}`,
        updatedIncidentArea
      );
      return resp.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.all });
    },
  });
};

export const useDeleteIncidentArea = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: UUID) => {
      await axios.delete(`${config.apiGateway.ics_incident_areas}/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.all });
    },
  });
};
